/**
 * ClipPhotoReplacementModal - A simplified photo replacement modal for clip context.
 *
 * This component handles logo replacement in Content Studio's clip preview,
 * where the main ReplacePhotoElementModal cannot be used because it relies on
 * useVideoCreatorStore() which returns the main store, not the clip adapter.
 */

import { useStore } from '@src/stores-v2/StoreContext';
import applyImageOptimizations from '@src/utility/applyImageOptimizations';
import { LogManager } from '@src/utility/Logger';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import Modal from '../common/Modal';
import PhotoModalTop from '../common/PhotoModalTop';
import PhotoModal, { type PhotoModalOtherFields } from '../common/PhotosModal';
import { PickerModalCard } from '../common/pickerModalStyles';
import { getReplaceMediaTitle } from '../common/replaceMediaTitle';

import type { ImageKey, ImageWithType } from '../../types.ts/general';
import type { FileData } from '../../types.ts/story';
import type { ClipVideoCreatorAdapter } from './hooks/ClipVideoCreatorAdapter';

const logger = LogManager.getLogger('component:ClipPhotoReplacementModal');

interface ClipPhotoReplacementModalProps {
  adapter: ClipVideoCreatorAdapter;
}

/**
 * Simplified photo replacement modal for clip context.
 * Handles logo replacement by observing the adapter's openPhotoElementReplacementModal.
 */
export const ClipPhotoReplacementModal = observer(
  ({ adapter }: ClipPhotoReplacementModalProps) => {
    const { datoClientStore } = useStore();
    const [isReplacing, setIsReplacing] = useState(false);

    const modalConfig = adapter.openPhotoElementReplacementModal;
    const element = modalConfig?.element;
    const isLogoControl = modalConfig?.isLogoControl;
    const isVideoControl = modalConfig?.isVideoControl;

    const [selectedImage, setSelectedImage] = useState<
      ImageWithType[ImageKey] | null
    >(null);

    const title =
      modalConfig?.title ||
      getReplaceMediaTitle({
        isLogo: isLogoControl,
        isVideo: isVideoControl,
      });

    const handleClose = () => {
      runInAction(() => {
        adapter.openPhotoElementReplacementModal = null;
      });
      setSelectedImage(null);
    };

    const handleReplacePhoto = async () => {
      if (!selectedImage?.url || !element) return;

      try {
        let imageUrl = applyImageOptimizations(selectedImage.url);

        let uploadId = selectedImage.id;

        // For AI images, upload to Dato first
        if (selectedImage.type === 'ai') {
          setIsReplacing(true);
          const altText = selectedImage.alt || 'AI generated image';
          const fileName = `ai-image-${Date.now()}`;
          const newPhotoData: FileData & { fileName: string } = {
            type: 'ai',
            url: selectedImage.url,
            fileName,
            alt: altText.slice(0, 150),
            title: altText.slice(0, 150),
          };
          const newUpload =
            await datoClientStore.assetRepository?.uploadFile(newPhotoData);
          if (newUpload) {
            imageUrl = newUpload.url;
            uploadId = newUpload.id;
          }
        }

        // Call the onImageSourceChange callback which handles the actual replacement
        modalConfig?.onImageSourceChange?.(imageUrl, uploadId);

        logger.info('Photo replaced in clip context', {
          isLogoControl,
          imageUrl: imageUrl.substring(0, 50) + '...',
          uploadId,
        });
      } catch (error) {
        logger.error('Error replacing photo in clip context', { error });
      } finally {
        setIsReplacing(false);
        handleClose();
      }
    };

    // Don't render if no element or modal config
    if (!element || !modalConfig) return null;

    const getOtherFields = (): PhotoModalOtherFields[] => {
      if (isLogoControl) {
        return ['org_logos'];
      } else if (isVideoControl) {
        return ['org_videos'];
      } else {
        return ['ai', 'stock', 'org_photos'];
      }
    };

    return (
      <Modal isOpen={true} closeModal={handleClose}>
        <PickerModalCard>
          <PhotoModal
            TopComponent={props => (
              <PhotoModalTop
                {...props}
                replaceAction={handleReplacePhoto}
                title={title}
                isSelected={!!selectedImage}
                selectedImage={selectedImage}
                isLoading={isReplacing}
                setSelectedImage={setSelectedImage}
              />
            )}
            showDescription
            otherFields={getOtherFields()}
            onCloseSelf={handleClose}
            selectedImageUrl={selectedImage?.url || null}
            setSelectedImage={setSelectedImage}
            origin="clip-templates"
          />
        </PickerModalCard>
      </Modal>
    );
  },
);

export default ClipPhotoReplacementModal;
